<template>
  <div class="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900/70 via-black/50 to-gray-900/70">
    <div class="max-w-7xl mx-auto">
      <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 p-6 relative">
        <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
        <div class="flex justify-between items-center relative">
          <h1 class="text-3xl font-bold text-amber-300 flex items-center">
            <i class="fas fa-chart-bar mr-3"></i> Statistiques
          </h1>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>

      <div v-else>
        <!-- Statistiques générales -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-gamepad mr-2"></i> Matchs totaux
              </h3>
            </div>
            <div class="p-6 flex items-center justify-center bg-black/80">
              <div class="text-5xl font-bold text-amber-300 animate-pulse-slow">{{ totalMatches }}</div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-users mr-2"></i> Joueurs classés
              </h3>
            </div>
            <div class="p-6 flex items-center justify-center bg-black/80">
              <div class="text-5xl font-bold text-amber-300 animate-pulse-slow">{{ totalPlayers }}</div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-trophy mr-2"></i> Score ELO moyen
              </h3>
            </div>
            <div class="p-6 flex items-center justify-center bg-black/80">
              <div class="text-5xl font-bold text-amber-300 animate-pulse-slow">{{ averageElo }}</div>
            </div>
          </div>
        </div>

        <!-- Top joueurs et matchs récents -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-crown mr-2"></i> Top 5 joueurs
              </h3>
            </div>
            <div class="p-4 relative">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <ul class="space-y-2 relative">
                <li v-for="(player, index) in topPlayers" :key="index" 
                    class="flex justify-between py-2 px-3 rounded-lg transform transition-transform duration-200 hover:scale-102"
                    :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
                  <div class="flex items-center">
                    <span class="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-700 to-amber-900 text-amber-300 mr-3 text-sm font-bold shadow-glow">{{ index + 1 }}</span>
                    <span class="text-amber-200 flex items-center">
                      <i class="fas fa-user-circle mr-2"></i>
                      {{ player?.username || player?.name || `Joueur ${index + 1}` }}
                    </span>
                  </div>
                  <span class="font-medium text-amber-300 px-3 py-1 bg-amber-900/40 rounded-full border border-amber-700/40 shadow-inner flex items-center">
                    <i class="fas fa-star mr-1 text-yellow-400"></i> {{ player?.elo || 1000 }} pts
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-history mr-2"></i> Derniers matchs
              </h3>
            </div>
            <div class="p-4 relative">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <ul class="space-y-2 relative">
                <li v-for="(match, index) in recentMatches" :key="index" 
                    class="py-2 px-3 rounded-lg transform transition-transform duration-200 hover:scale-102"
                    :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
                  <div class="flex justify-between mb-1">
                    <span class="text-amber-200 text-sm flex items-center">
                      <i class="fas fa-calendar-alt mr-1"></i> {{ formatDate(match.date) }}
                    </span>
                    <span class="text-xs text-amber-300/70 px-2 py-0.5 bg-amber-900/30 rounded-full border border-amber-700/30">
                      <i class="fas fa-check-circle mr-1"></i> {{ match.status || 'Terminé' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <div class="text-amber-200 flex items-center">
                      <i class="fas fa-user mr-2 text-amber-400"></i>
                      {{ match.user1?.username || 'Joueur 1' }}
                    </div>
                    <div class="flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-amber-900/20 via-amber-800/30 to-amber-900/20 rounded-lg border border-amber-700/20">
                      <span class="font-semibold text-amber-300">{{ match.scoreUser1 || 0 }}</span>
                      <span class="text-amber-500 mx-1">VS</span>
                      <span class="font-semibold text-amber-300">{{ match.scoreUser2 || 0 }}</span>
                    </div>
                    <div class="text-amber-200 flex items-center">
                      {{ match.user2?.username || 'Joueur 2' }}
                      <i class="fas fa-user ml-2 text-amber-400"></i>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Armées et statistiques de matchs -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-shield-alt mr-2"></i> Armées les plus jouées
              </h3>
            </div>
            <div class="p-4 relative">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <ul class="space-y-2 relative">
                <li v-for="(army, index) in topArmies" :key="index" 
                    class="flex justify-between py-2 px-3 rounded-lg transform transition-transform duration-200 hover:scale-102"
                    :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
                  <span class="text-amber-200 flex items-center">
                    <i class="fas fa-flag mr-2"></i> {{ army.name }}
                  </span>
                  <span class="font-medium text-amber-300 px-2 py-0.5 bg-amber-900/30 rounded-full border border-amber-700/30">{{ army.count }} matchs</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-flag mr-2"></i> Taux de victoire des factions
              </h3>
            </div>
            <div class="p-4 relative">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <div class="space-y-3">
                <div v-for="faction in topFactionsWinrate" :key="faction.name"
                     class="py-2 px-3 rounded-lg bg-black/80 flex flex-col">
                  <span class="text-amber-200 mb-1 flex items-center">
                    <i class="fas fa-flag mr-2"></i> {{ faction.name }}
                  </span>
                  <div class="w-full bg-amber-900/20 rounded-full h-4 overflow-hidden">
                    <div class="bg-gradient-to-r from-amber-600 to-yellow-500 h-4 rounded-full shadow-[0_0_8px_rgba(217,119,6,0.6)]"
                         :style="`width: ${faction.winrate}%`"></div>
                  </div>
                  <span class="text-amber-300 text-sm mt-1 font-bold">{{ faction.winrate }}%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
            <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
              <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
              <h3 class="text-xl font-bold text-amber-300 flex items-center relative">
                <i class="fas fa-users mr-2"></i> Joueurs les plus actifs ce mois
              </h3>
            </div>
            <div class="p-4 relative">
              <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
              <ul class="space-y-2 relative">
                <li v-for="(player, index) in activePlayersThisMonth" :key="player.id"
                    class="flex justify-between py-2 px-3 rounded-lg transform transition-transform duration-200 hover:scale-102"
                    :class="{ 'bg-black/80': index % 2 === 0, 'bg-amber-950/40': index % 2 !== 0 }">
                  <span class="text-amber-200 flex items-center">
                    <i class="fas fa-user mr-2"></i> {{ player.username || player.name || 'Joueur' }}
                  </span>
                  <span class="font-medium text-amber-300 px-2 py-0.5 bg-amber-900/30 rounded-full border border-amber-700/30 shadow-inner">{{ player.count }} matchs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const loading = ref(true)

// Données récupérées directement des stores
const allMatches = computed(() => store.getters['match/allMatches'] || [])
const allClassement = computed(() => store.getters['classement/allClassement'] || [])
const topPlayers = computed(() => {
  const players = store.getters['classement/topPlayers'] || []
  return players.slice(0, 5)
})

// Statistiques calculées
const totalMatches = computed(() => allMatches.value.length)
const totalPlayers = computed(() => allClassement.value.length)
const averageElo = computed(() => {
  if (allClassement.value.length === 0) return 1000
  const totalElo = allClassement.value.reduce((sum, player) => sum + (player.elo || 1000), 0)
  return Math.round(totalElo / allClassement.value.length)
})

// Matchs récents
const recentMatches = computed(() => {
  if (!allMatches.value || allMatches.value.length === 0) return []
  
  return [...allMatches.value]
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    .slice(0, 5)
})

// Armées les plus jouées
const topArmies = computed(() => {
  if (!allMatches.value || allMatches.value.length === 0) {
    return [
      { name: "Aucune donnée disponible", count: 0 }
    ]
  }
  
  const armyCounts = {}
  allMatches.value.forEach(match => {
    if (match.armee1) {
      armyCounts[match.armee1] = (armyCounts[match.armee1] || 0) + 1
    }
    if (match.armee2) {
      armyCounts[match.armee2] = (armyCounts[match.armee2] || 0) + 1
    }
  })
  
  const result = Object.entries(armyCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    
  return result.length > 0 ? result : [{ name: "Aucune donnée disponible", count: 0 }]
})

// Calcul du taux de victoire des factions (top 5)
const topFactionsWinrate = computed(() => {
  if (!allMatches.value || allMatches.value.length === 0) return [];
  const stats = {};
  allMatches.value.forEach(match => {
    // Faction 1
    if (match.armee1) {
      if (!stats[match.armee1]) stats[match.armee1] = { name: match.armee1, win: 0, total: 0 };
      stats[match.armee1].total++;
      if (match.scoreUser1 > match.scoreUser2) stats[match.armee1].win++;
    }
    // Faction 2
    if (match.armee2) {
      if (!stats[match.armee2]) stats[match.armee2] = { name: match.armee2, win: 0, total: 0 };
      stats[match.armee2].total++;
      if (match.scoreUser2 > match.scoreUser1) stats[match.armee2].win++;
    }
  });
  // Calcul du winrate
  const winrates = Object.values(stats).map(f => ({
    name: f.name,
    winrate: f.total > 0 ? Math.round((f.win / f.total) * 100) : 0
  }));
  // Trier par winrate décroissant et ne garder que le top 5
  return winrates.sort((a, b) => b.winrate - a.winrate).slice(0, 5);
})

// Calculer les joueurs les plus actifs du mois courant
const activePlayersThisMonth = computed(() => {
  if (!allMatches.value || allMatches.value.length === 0) return [];
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const playerCounts = {};
  allMatches.value.forEach(match => {
    if (!match.date) return;
    const date = new Date(match.date);
    if (date.getMonth() === month && date.getFullYear() === year) {
      // Compter pour user1
      if (match.user1) {
        const id = match.user1.id || match.user1._id || match.user1.username || match.user1.name;
        if (!playerCounts[id]) playerCounts[id] = { ...match.user1, count: 0 };
        playerCounts[id].count++;
      }
      // Compter pour user2
      if (match.user2) {
        const id = match.user2.id || match.user2._id || match.user2.username || match.user2.name;
        if (!playerCounts[id]) playerCounts[id] = { ...match.user2, count: 0 };
        playerCounts[id].count++;
      }
    }
  });
  // Retourner trié du plus actif au moins actif
  return Object.values(playerCounts).sort((a, b) => b.count - a.count);
})

// Formater la date
const formatDate = (dateString) => {
  if (!dateString) return "Date inconnue"
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch (error) {
    return "Date invalide"
  }
}

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('match/fetchAllMatches'),
      store.dispatch('classement/fetchClassement'),
      store.dispatch('classement/fetchTopPlayers'),
      store.dispatch('classement/fetchGlobalStats')
    ])
  } catch (error) {
    console.error('Erreur lors du chargement des données', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.pattern-warhammer {
  background-image: 
    url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0L0 12l12 12 12-12z' fill='%23D4AF37' fill-opacity='0.2'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23D4AF37' fill-opacity='0.1'/%3E%3C/svg%3E");
  background-size: 30px 30px, 60px 60px;
}

.box-glow {
  box-shadow: 0 0 30px rgba(217, 119, 6, 0.15), 
              0 5px 20px rgba(0, 0, 0, 0.3), 
              inset 0 0 15px rgba(217, 119, 6, 0.05);
  transition: box-shadow 0.3s ease-in-out;
}

.box-glow:hover {
  box-shadow: 0 0 35px rgba(217, 119, 6, 0.25), 
              0 5px 20px rgba(0, 0, 0, 0.4), 
              inset 0 0 20px rgba(217, 119, 6, 0.1);
}

.shadow-glow {
  box-shadow: 0 0 10px rgba(217, 119, 6, 0.3);
}

.animate-pulse-slow {
  animation: pulse 3s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.hover\:scale-102:hover {
  transform: scale(1.02);
}
</style> 